import { useState } from "react";
import { Link } from "react-router-dom";
import { Palette, Type, Download, Loader2, Sparkles, ArrowLeft, Star, Check } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NameColoringGenerator = () => {
  const [name, setName] = useState("");
  const [style, setStyle] = useState("fun");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generationsLeft, setGenerationsLeft] = useState(3);

  const styles = [
    { id: "fun", label: "Fun & Playful", desc: "Cartoon style with decorations" },
    { id: "elegant", label: "Elegant Script", desc: "Fancy cursive lettering" },
    { id: "bold", label: "Bold Block", desc: "Big chunky letters" },
    { id: "floral", label: "Floral", desc: "Letters with flower decorations" }
  ];

  const handleGenerate = async () => {
    if (!name.trim()) {
      toast.error("Please enter a name");
      return;
    }

    if (generationsLeft <= 0) {
      toast.error("You've used all free generations. Sign up for more!");
      return;
    }

    setIsGenerating(true);
    
    try {
      // Build the prompt based on style
      let prompt = "";
      switch (style) {
        case "fun":
          prompt = `Coloring page with the name "${name.toUpperCase()}" in fun cartoon bubble letters, decorated with stars and swirls, black and white line art for coloring, child-friendly`;
          break;
        case "elegant":
          prompt = `Coloring page with the name "${name}" in elegant flowing cursive script, decorative flourishes, black and white line art for coloring`;
          break;
        case "bold":
          prompt = `Coloring page with the name "${name.toUpperCase()}" in big bold block letters, 3D effect, black and white line art for coloring`;
          break;
        case "floral":
          prompt = `Coloring page with the name "${name}" in decorative letters filled with flowers and leaves, botanical style, black and white line art for coloring`;
          break;
        default:
          prompt = `Coloring page with the name "${name}" in decorative letters, black and white line art for coloring`;
      }

      const response = await axios.post(`${BACKEND_URL}/api/generate/free-tool`, {
        prompt: prompt,
        tool_type: "name_generator"
      });

      if (response.data.image_url) {
        setGeneratedImage(response.data.image_url);
        setGenerationsLeft(prev => prev - 1);
        toast.success("Coloring page generated!");
      }
    } catch (error) {
      console.error("Generation error:", error);
      if (error.response?.status === 429) {
        toast.error("Rate limit reached. Please try again later or sign up!");
      } else {
        toast.error("Failed to generate. Please try again.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (generatedImage) {
      const link = document.createElement("a");
      link.href = generatedImage;
      link.download = `${name}-coloring-page.png`;
      link.click();
      toast.success("Download started!");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/free-tools" className="text-slate-600 hover:text-slate-900 font-medium hidden md:flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                All Tools
              </Link>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Link to="/free-tools" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Free Tools
            </Link>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full mb-4">
              <Type className="w-4 h-4 text-violet-600" />
              <span className="text-sm font-medium text-violet-700">Free Name Generator</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Name Coloring Page Generator
            </h1>
            
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Create personalized coloring pages with any name. Perfect for birthdays, 
              classroom activities, or custom gifts!
            </p>
          </div>

          {/* Generator Card */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-6">Create Your Page</h2>
              
              {/* Name Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Enter Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Emma, Jacob, Sofia"
                  maxLength={15}
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition-all text-lg"
                  data-testid="name-input"
                />
                <p className="text-xs text-slate-500 mt-1">{name.length}/15 characters</p>
              </div>

              {/* Style Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Choose Style
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {styles.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStyle(s.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        style === s.id
                          ? "border-violet-500 bg-violet-50"
                          : "border-slate-200 hover:border-violet-200"
                      }`}
                      data-testid={`style-${s.id}`}
                    >
                      <div className="font-medium text-slate-800 text-sm">{s.label}</div>
                      <div className="text-xs text-slate-500">{s.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !name.trim()}
                className="w-full h-14 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold text-lg disabled:opacity-50"
                data-testid="generate-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Coloring Page
                  </>
                )}
              </Button>

              {/* Generations Counter */}
              <div className="mt-4 text-center">
                <span className="text-sm text-slate-500">
                  {generationsLeft} free generations remaining
                </span>
                {generationsLeft <= 1 && (
                  <Link to="/signup" className="block text-violet-600 text-sm font-medium mt-1 hover:underline">
                    Sign up for unlimited generations →
                  </Link>
                )}
              </div>
            </div>

            {/* Preview Section */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-6">Preview</h2>
              
              <div className="aspect-square rounded-xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden">
                {generatedImage ? (
                  <img
                    src={generatedImage}
                    alt={`${name} coloring page`}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center p-8">
                    <Type className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Your coloring page will appear here</p>
                    <p className="text-sm text-slate-400 mt-2">Enter a name and click generate</p>
                  </div>
                )}
              </div>

              {/* Download Button */}
              {generatedImage && (
                <Button
                  onClick={handleDownload}
                  className="w-full h-12 mt-4 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold"
                  data-testid="download-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Free
                </Button>
              )}
            </div>
          </div>

          {/* Features Section */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Why Use Our Name Generator?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Personalized", desc: "Create unique coloring pages with any name - perfect for making kids feel special!" },
                { title: "Multiple Styles", desc: "Choose from fun, elegant, bold, or floral styles to match any occasion." },
                { title: "Print Ready", desc: "High-quality output that prints beautifully on standard paper." }
              ].map((feature, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center mb-4">
                    <Check className="w-5 h-5 text-violet-600" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Want More Than Just Names?
            </h2>
            <p className="text-violet-100 mb-6 max-w-xl mx-auto">
              With ColorWonder, create ANY coloring page you can imagine. 
              Dinosaurs, unicorns, space adventures - just describe it!
            </p>
            <Button
              onClick={() => window.location.href = "/signup"}
              className="h-14 px-8 rounded-full bg-white text-violet-600 font-semibold text-lg hover:bg-violet-50"
            >
              Try Full Generator Free →
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© 2025 ColorWonder. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/free-tools" className="hover:text-slate-700">Free Tools</Link>
            <Link to="/blog" className="hover:text-slate-700">Blog</Link>
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NameColoringGenerator;
